import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { CContainer, CButton, CSpinner, CCard, CCardBody } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilMap, cilReload } from "@coreui/icons";
import { toast } from "react-toastify";
import { YMaps, Map, Placemark, ZoomControl, TypeSelector, Clusterer } from "@pbe/react-yandex-maps";
import dayjs from "dayjs";

const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return "00:00";
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${m}м ${s}с`;
};

// Константы для кэширования
const CACHE_KEY = "omnicomm_map_cache";
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 минуты "свежести" данных
const COOLDOWN_SEC = 60; // 60 секунд нельзя спамить кнопку обновить

const OmnicommMapPage = () => {
    const [trucks, setTrucks] = useState([]);
    const [analyticsMap, setAnalyticsMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);

    // Состояние таймера блокировки кнопки
    const [cooldown, setCooldown] = useState(0);

    // Таймер для кулдауна кнопки
    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(prev => prev - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const fetchAnalytics = async () => {
        try {
            const token = localStorage.getItem("token");
            const start = dayjs().startOf("day").toISOString();
            const end = dayjs().endOf("day").toISOString();

            const res = await axios.get(
                `/api/analytics/trucks/?start_date=${start}&end_date=${end}&show_unknown=false`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const statsMap = {};
            if (res.data && res.data.trucks_summary) {
                res.data.trucks_summary.forEach(t => { statsMap[t.id] = t; });
            }
            setAnalyticsMap(statsMap);
        } catch (e) {
            console.warn("Не удалось загрузить аналитику", e);
        }
    };

    const fetchLive = async (forceUpdate = false, showSilent = false) => {
        // 1. Пробуем загрузить из кэша (если это не принудительное обновление)
        if (!forceUpdate) {
            const cachedStr = localStorage.getItem(CACHE_KEY);
            if (cachedStr) {
                try {
                    const cachedData = JSON.parse(cachedStr);
                    const ageMs = Date.now() - cachedData.savedAt;

                    // Если данные свежее CACHE_TTL_MS (3 минуты) -> берем из кэша
                    if (ageMs < CACHE_TTL_MS) {
                        setTrucks(cachedData.trucks);
                        setLastUpdate(new Date(cachedData.savedAt));

                        // Если мы недавно обновили, вешаем остаток кулдауна на кнопку
                        const remainingCooldown = Math.floor((COOLDOWN_SEC * 1000 - ageMs) / 1000);
                        if (remainingCooldown > 0) {
                            setCooldown(remainingCooldown);
                        }

                        setLoading(false);
                        return; // Прерываем функцию, запрос к API не идет!
                    }
                } catch (e) {
                    console.warn("Ошибка чтения кэша Omnicomm", e);
                }
            }
        }

        // 2. Если кэш протух или нажата кнопка "Обновить" (forceUpdate = true)
        if (!showSilent) setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get("/api/fleet/omnicomm-live/", {
                headers: { Authorization: `Bearer ${token}` },
            });

            const newData = res.data;
            const now = Date.now();

            setTrucks(newData);
            setLastUpdate(new Date(now));

            // Сохраняем новые данные в кэш браузера
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                savedAt: now,
                trucks: newData
            }));

            // Блокируем кнопку обновления на 60 секунд для защиты от спама
            setCooldown(COOLDOWN_SEC);

        } catch (e) {
            toast.error(e.response?.data?.error || "Ошибка загрузки данных с Omnicomm");
        } finally {
            if (!showSilent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
        fetchLive(false); // Загружаем (сначала проверит кэш)

        // Фоновое обновление раз в 3 минуты (чтобы не убить лимиты)
        const interval = setInterval(() => {
            fetchLive(true, true);
            fetchAnalytics();
        }, CACHE_TTL_MS);

        return () => clearInterval(interval);
    }, []);

    const handleManualUpdate = () => {
        if (cooldown > 0) return; // Защита
        fetchAnalytics();
        fetchLive(true); // forceUpdate = true (игнорирует кэш)
    };

    const generateBalloonHTML = (truck) => {
        const stat = analyticsMap[truck.id];
        const hasStats = !!stat;
        const color = truck.color || "#321fdb";

        return `
      <div style="font-family: 'Montserrat', sans-serif; min-width: 250px; margin: -15px -15px -15px -15px;">
        <div style="background-color: ${color}; color: white; padding: 12px 15px; display: flex; justify-content: space-between; align-items: center; border-radius: 8px 8px 0 0;">
          <strong style="font-size: 14px; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px;" title="${truck.brand}">
            ${truck.brand}
          </strong>
          <span style="font-size: 11px; background: rgba(0,0,0,0.25); padding: 3px 8px; border-radius: 4px; font-weight: 600; white-space: nowrap;">
            ${truck.plate_number}
          </span>
        </div>
        
        <div style="padding: 15px; background: white; border-radius: 0 0 8px 8px;">
          <div style="font-size: 11px; color: #6c757d; margin-bottom: 12px; display: flex; align-items: center;">
            <span style="display: inline-block; width: 8px; height: 8px; background-color: #2eb85c; border-radius: 50%; margin-right: 6px; box-shadow: 0 0 4px #2eb85c;"></span>
            Координаты: <b>${dayjs(truck.timestamp).format("HH:mm:ss")}</b>
          </div>

          ${hasStats ? `
            <div style="border-top: 1px dashed #dee2e6; padding-top: 12px;">
              <div style="font-size: 10px; text-transform: uppercase; color: #adb5bd; font-weight: bold; margin-bottom: 10px; letter-spacing: 0.5px;">
                Показатели за сегодня
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 13px;">
                <span style="color: #495057;">Сделано рейсов:</span> 
                <strong>${stat.arrivals} шт.</strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 13px;">
                <span style="color: #495057;">Загружено ковшей:</span> 
                <strong>${stat.cycles} шт. <span style="color:#adb5bd; font-size:10px;">(~${(stat.cycles / stat.arrivals).toFixed(1)}/рейс)</span></strong>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px;">
                <span style="color: #495057;">Ср. время загрузки:</span> 
                <strong>${formatDuration(stat.total_time_sec / stat.arrivals)}</strong>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 10px; border-top: 1px solid #f8f9fa;">
                <span style="color: #495057; font-weight: bold;">Текущий объем:</span> 
                <strong style="color: #2eb85c; font-size: 16px;">${Number(stat.volume).toFixed(1)} м³</strong>
              </div>
            </div>
          ` : `
            <div style="border-top: 1px dashed #dee2e6; padding-top: 15px; padding-bottom: 5px; text-align: center;">
              <span style="color: #adb5bd; font-size: 12px; font-style: italic;">За сегодня погрузок не зафиксировано</span>
            </div>
          `}
        </div>
      </div>
    `;
    };

    return (
        <div className="bg-light min-vh-100 p-3 p-md-4">
            <CContainer fluid>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                    <h2 className="d-flex align-items-center mb-0 fw-bold">
                        <CIcon icon={cilMap} className="text-primary me-3" size="xl" />
                        Карта самосвалов (Omnicomm)
                    </h2>
                    <div className="d-flex align-items-center gap-3 mt-3 mt-md-0">
                        {lastUpdate && (
                            <span className="text-muted small fw-bold" title="Время актуальности данных">
                                Кэш: {dayjs(lastUpdate).format("HH:mm:ss")}
                            </span>
                        )}

                        {/* Кнопка с кулдауном */}
                        <CButton
                            color="primary"
                            variant={cooldown > 0 ? "ghost" : "outline"}
                            size="sm"
                            className="fw-bold"
                            onClick={handleManualUpdate}
                            disabled={loading || cooldown > 0}
                            style={{ minWidth: "130px" }}
                        >
                            {loading ? (
                                <CSpinner size="sm" />
                            ) : cooldown > 0 ? (
                                `Ждите (${cooldown}с)`
                            ) : (
                                <><CIcon icon={cilReload} className="me-1" /> Обновить</>
                            )}
                        </CButton>

                        <CButton color="secondary" as={Link} to="/dashboard" size="sm" className="fw-bold">
                            <CIcon icon={cilArrowLeft} className="me-2" /> Назад
                        </CButton>
                    </div>
                </div>

                <CCard className="shadow-sm border-0">
                    <CCardBody className="p-0">
                        <div style={{ height: "calc(100vh - 150px)", borderRadius: "8px", overflow: "hidden" }}>
                            <YMaps query={{ lang: "ru_RU" }}>
                                <Map
                                    defaultState={{ center: [55.751574, 37.573856], zoom: 4 }}
                                    width="100%"
                                    height="100%"
                                    options={{ minZoom: 2 }}
                                >
                                    <Clusterer
                                        options={{
                                            preset: "islands#invertedNightClusterIcons",
                                            groupByCoordinates: false,
                                        }}
                                        modules={["clusterer.addon.balloon"]}
                                    >
                                        {trucks.map((truck) => (
                                            <Placemark
                                                key={truck.id}
                                                geometry={[truck.lat, truck.lon]}
                                                properties={{
                                                    iconCaption: `${truck.brand} (${truck.plate_number})`,
                                                    hintContent: `<strong>${truck.brand}</strong> (${truck.plate_number})`,
                                                    balloonContent: generateBalloonHTML(truck),
                                                }}
                                                options={{
                                                    preset: "islands#circleDotIcon",
                                                    iconColor: truck.color || "#28a745",
                                                    hideIconOnBalloonOpen: false,
                                                    balloonOffset: [0, -20],
                                                    balloonPanelMaxMapArea: 0
                                                }}
                                                modules={["geoObject.addon.balloon", "geoObject.addon.hint"]}
                                            />
                                        ))}
                                    </Clusterer>
                                    <ZoomControl options={{ float: "right" }} />
                                    <TypeSelector options={{ float: "right" }} />
                                </Map>
                            </YMaps>
                        </div>
                    </CCardBody>
                </CCard>
            </CContainer>
        </div>
    );
};

export default OmnicommMapPage;